import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useGrowStore } from "../../../hooks/useGrowStore";
import { exploreClasses } from "../../../mocks/explore";

const DIMENSION_META = {
  creativity: { label: "창의성", icon: "ri-sparkling-2-line", color: "#FF8C00", desc: "프롬프트의 독창성과 참신함", classIds: [1, 4, 6] },
  specificity: { label: "구체성", icon: "ri-focus-3-line", color: "#E91E63", desc: "프롬프트의 명확성과 세밀함", classIds: [2, 3, 7] },
  completeness: { label: "완성도", icon: "ri-check-double-line", color: "#00D873", desc: "결과물의 완성도와 구성력", classIds: [5, 8] },
};

type DimKey = keyof typeof DIMENSION_META;

function RadarBar({ label, value, color, icon, isWeakest }: { label: string; value: number; color: string; icon: string; isWeakest: boolean }) {
  return (
    <div className={`rounded-2xl p-4 transition-all ${isWeakest ? "bg-red-50 border border-red-100" : "bg-[#F9F9F9]"}`}>
      <div className="flex items-center gap-2 mb-2.5">
        <div className="w-7 h-7 flex items-center justify-center rounded-lg" style={{ backgroundColor: `${color}20` }}>
          <i className={`${icon} text-sm`} style={{ color }} />
        </div>
        <span className="text-xs font-extrabold text-[#191F28]">{label}</span>
        {isWeakest && (
          <span className="ml-auto text-[10px] font-bold text-red-500 bg-red-50 px-2 py-0.5 rounded-full border border-red-100">약점</span>
        )}
        {!isWeakest && value > 0 && (
          <span className="ml-auto text-xs font-black" style={{ color }}>{value}점</span>
        )}
      </div>
      <div className="h-2.5 bg-white rounded-full overflow-hidden border border-[#F2F4F6]">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: value > 0 ? `${value}%` : "0%", backgroundColor: color }}
        />
      </div>
      {value > 0 ? (
        <div className="flex items-center justify-between mt-1.5">
          <span className="text-[10px] text-[#8B95A1]">평균</span>
          <span className="text-xs font-black" style={{ color }}>{value}점</span>
        </div>
      ) : (
        <p className="text-[10px] text-[#C8CDD4] mt-1.5">아직 데이터 없음</p>
      )}
    </div>
  );
}

export default function WeaknessAnalysisSection() {
  const { store, isClassCompleted } = useGrowStore();
  const navigate = useNavigate();

  const analysis = useMemo(() => {
    if (!store.completedClasses.length) return null;

    const sums = { creativity: 0, specificity: 0, completeness: 0 };
    const count = store.completedClasses.length;

    store.completedClasses.forEach((c) => {
      sums.creativity += c.scores.creativity;
      sums.specificity += c.scores.specificity;
      sums.completeness += c.scores.completeness;
    });

    const avgs: Record<DimKey, number> = {
      creativity: Math.round(sums.creativity / count),
      specificity: Math.round(sums.specificity / count),
      completeness: Math.round(sums.completeness / count),
    };

    const weakest = (Object.keys(avgs) as DimKey[]).reduce((a, b) => (avgs[a] < avgs[b] ? a : b));
    const strongest = (Object.keys(avgs) as DimKey[]).reduce((a, b) => (avgs[a] > avgs[b] ? a : b));

    const recClassIds = DIMENSION_META[weakest].classIds;
    const recClasses = exploreClasses
      .filter((c) => recClassIds.includes(c.id) && !isClassCompleted(c.id))
      .slice(0, 2);

    // If all weak classes completed, show any unfinished class
    const fallback = recClasses.length === 0
      ? exploreClasses.filter((c) => !isClassCompleted(c.id)).slice(0, 2)
      : recClasses;

    return { avgs, weakest, strongest, recClasses: fallback };
  }, [store.completedClasses, isClassCompleted]);

  if (!analysis) {
    return (
      <div className="bg-white rounded-3xl p-7">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-black text-[#191F28]">약점 분석 & 재학습 추천</h3>
            <p className="text-xs text-[#8B95A1] mt-0.5">클래스를 완료하면 AI가 약점을 분석해드려요</p>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center py-10 bg-[#F9F9F9] rounded-2xl">
          <div className="w-16 h-16 rounded-2xl bg-[#F2F4F6] flex items-center justify-center mb-4">
            <i className="ri-brain-line text-3xl text-[#C8CDD4]" />
          </div>
          <p className="text-sm font-bold text-[#8B95A1] mb-1">아직 분석 데이터가 없어요</p>
          <p className="text-xs text-[#C8CDD4] mb-4">첫 번째 클래스를 완료해보세요!</p>
          <button
            onClick={() => navigate("/explore")}
            className="bg-[#00D873] text-white text-sm font-bold px-6 py-3 rounded-full cursor-pointer hover:opacity-90 whitespace-nowrap"
          >
            클래스 시작하기 →
          </button>
        </div>
      </div>
    );
  }

  const { avgs, weakest, strongest, recClasses } = analysis;
  const weakMeta = DIMENSION_META[weakest];
  const strongMeta = DIMENSION_META[strongest];

  return (
    <div className="bg-white rounded-3xl p-7">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-black text-[#191F28]">약점 분석 & 재학습 추천</h3>
          <p className="text-xs text-[#8B95A1] mt-0.5">AI가 분석한 나의 프롬프트 역량</p>
        </div>
        <div className="text-right">
          <div className="text-xs text-[#8B95A1]">분석 기반</div>
          <div className="text-sm font-extrabold text-[#191F28]">{store.completedClasses.length}개 클래스</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Dimension scores */}
        <div>
          <p className="text-sm font-extrabold text-[#191F28] mb-3">역량별 평균 점수</p>
          <div className="space-y-3">
            {(Object.keys(DIMENSION_META) as DimKey[]).map((key) => (
              <RadarBar
                key={key}
                label={DIMENSION_META[key].label}
                value={avgs[key]}
                color={DIMENSION_META[key].color}
                icon={DIMENSION_META[key].icon}
                isWeakest={key === weakest}
              />
            ))}
          </div>

          {/* Summary callouts */}
          <div className="mt-4 space-y-2.5">
            <div className="flex items-start gap-3 bg-red-50 rounded-xl p-3.5">
              <div className="w-6 h-6 flex items-center justify-center shrink-0 mt-0.5">
                <i className={`${weakMeta.icon} text-red-400`} />
              </div>
              <div>
                <p className="text-xs font-extrabold text-red-500">약점: {weakMeta.label} ({avgs[weakest]}점)</p>
                <p className="text-[11px] text-red-400 mt-0.5">{weakMeta.desc}이 상대적으로 낮아요</p>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-[#E8F5E9] rounded-xl p-3.5">
              <div className="w-6 h-6 flex items-center justify-center shrink-0 mt-0.5">
                <i className={`${strongMeta.icon} text-[#00D873]`} />
              </div>
              <div>
                <p className="text-xs font-extrabold text-[#00D873]">강점: {strongMeta.label} ({avgs[strongest]}점)</p>
                <p className="text-[11px] text-[#00D873]/70 mt-0.5">이 영역은 이미 잘 하고 있어요 👏</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Recommendations */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <p className="text-sm font-extrabold text-[#191F28]">추천 재학습 클래스</p>
            <span
              className="text-[10px] font-bold px-2 py-0.5 rounded-full"
              style={{ backgroundColor: `${weakMeta.color}20`, color: weakMeta.color }}
            >
              {weakMeta.label} 강화
            </span>
          </div>

          {recClasses.length === 0 ? (
            <div className="bg-[#E8F5E9] rounded-2xl p-5 text-center">
              <p className="text-sm font-bold text-[#00D873] mb-1">🎉 모든 추천 클래스 완료!</p>
              <p className="text-xs text-[#8B95A1]">당신은 이미 균형 잡힌 실력자예요</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recClasses.map((cls, i) => (
                <div key={cls.id} className="bg-[#F9F9F9] rounded-2xl p-4 border border-[#F2F4F6] hover:border-[#00D873]/30 transition-all">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: cls.iconBg }}>
                      <i className={`${cls.icon} text-base`} style={{ color: cls.iconColor }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-bold text-[#8B95A1] uppercase tracking-wide">{cls.level}</span>
                        {i === 0 && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: `${weakMeta.color}20`, color: weakMeta.color }}>
                            강력 추천
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-extrabold text-[#191F28] truncate">{cls.title}</p>
                      <p className="text-[11px] text-[#8B95A1] mt-0.5 line-clamp-2">{cls.desc}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-[#8B95A1] flex items-center gap-1">
                      <i className="ri-time-line" />{cls.duration}
                    </span>
                    <button
                      onClick={() => navigate(`/learn/${cls.id}`)}
                      className="text-xs font-extrabold px-4 py-1.5 rounded-full text-white cursor-pointer hover:opacity-90 transition-all whitespace-nowrap"
                      style={{ backgroundColor: weakMeta.color }}
                    >
                      지금 시작 →
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* AI insight box */}
          <div className="mt-4 bg-gradient-to-r from-[#191F28] to-[#2D3748] rounded-2xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-[#00D873] flex items-center justify-center flex-shrink-0">
                <i className="ri-robot-2-line text-white text-sm" />
              </div>
              <div>
                <p className="text-xs font-bold text-[#00D873] mb-1">GrowSeo AI 분석</p>
                <p className="text-xs text-white/80 leading-relaxed">
                  {weakMeta.label}을 높이려면 프롬프트에 <span className="text-[#00D873] font-bold">{weakest === "creativity" ? "감성 형용사·스타일 키워드" : weakest === "specificity" ? "수치·역할·형식 지정" : "단계별 구조와 예시"}</span>를 추가해보세요.
                  {`${avgs[weakest]}`}점 → 85점 달성이 목표예요!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
